import React, { useContext, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import { fmtUSD } from "../../utils/reports/format";
import { isDownRoom, itemsNeededForRoom } from "../../utils/reports/roomCost";

/**
 * Cost-to-Make-Ready (portfolio) — total item cost to bring every down room up,
 * aggregated by vendor with a line per item, vendor subtotals, and grand total.
 */
export default function GenerateCostToMakeReady({ user }) {
  const { rooms, items } = useContext(PropertyContext);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const downRooms = (rooms || []).filter(isDownRoom);

      // Aggregate item needs across all down rooms, keyed by vendor + item.
      const byVendor = new Map(); // vendorName -> Map(itemName -> {qty, unitCost})
      downRooms.forEach((room) => {
        itemsNeededForRoom(room, items).forEach((line) => {
          if (!byVendor.has(line.vendorName)) byVendor.set(line.vendorName, new Map());
          const itemMap = byVendor.get(line.vendorName);
          const existing = itemMap.get(line.itemName) || { qty: 0, unitCost: line.unitCost };
          existing.qty += line.qty;
          existing.unitCost = line.unitCost;
          itemMap.set(line.itemName, existing);
        });
      });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "Cost to Make Ready",
        dateRange: `${downRooms.length} down room(s)`,
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      const tableBody = [];
      let grandTotal = 0;

      Array.from(byVendor.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([vendorName, itemMap]) => {
          const rows = Array.from(itemMap.entries())
            .map(([itemName, v]) => ({
              itemName,
              qty: v.qty,
              unitCost: v.unitCost,
              lineTotal: v.qty * v.unitCost,
            }))
            .sort((a, b) => a.itemName.localeCompare(b.itemName));
          const subtotal = rows.reduce((s, r) => s + r.lineTotal, 0);
          grandTotal += subtotal;

          tableBody.push([
            { content: vendorName, colSpan: 3, styles: { fontStyle: "bold", fillColor: [230, 230, 230] } },
            { content: `Subtotal: ${fmtUSD(subtotal)}`, styles: { fontStyle: "bold", fillColor: [230, 230, 230], halign: "right" } },
          ]);
          rows.forEach((r) => {
            tableBody.push([r.itemName, String(r.qty), fmtUSD(r.unitCost), fmtUSD(r.lineTotal)]);
          });
        });

      if (tableBody.length === 0) {
        tableBody.push([{ content: "No items needed across down rooms.", colSpan: 4, styles: { fontStyle: "italic" } }]);
      } else {
        tableBody.push([
          { content: "GRAND TOTAL", colSpan: 3, styles: { fontStyle: "bold", fillColor: [40, 40, 40], textColor: 255 } },
          { content: fmtUSD(grandTotal), styles: { fontStyle: "bold", fillColor: [40, 40, 40], textColor: 255, halign: "right" } },
        ]);
      }

      autoTable(pdf, {
        startY: contentTop,
        head: [["Item", "Qty", "Unit Cost", "Line Total"]],
        body: tableBody,
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [60, 60, 60], textColor: 255 },
        columnStyles: {
          1: { halign: "right", cellWidth: 20 },
          2: { halign: "right", cellWidth: 30 },
          3: { halign: "right", cellWidth: 32 },
        },
        margin: { left: 12, right: 12 },
        theme: "grid",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("Cost_To_Make_Ready", { ext: "pdf" })
      );
    } catch (e) {
      console.error("Error generating Cost to Make Ready:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      fullWidth
      onClick={generate}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={16} /> : null}
    >
      {loading ? "Generating..." : "Cost to Make Ready"}
    </Button>
  );
}
